import type { UserId } from "./ids.js";

export type WeatherCondition = "unknown" | "clear" | "cloudy" | "rain" | "storm" | "fog" | "wind";
export type WeatherSource = "manual" | "game_server" | "unknown";
export type LivingWorldEffect = "none" | "snow_frost_preview" | "aurora_2d" | "stormfront" | "snowfront" | "dustfront" | "marshveil";

export function isRegionalWeatherFront(effect: string | undefined): effect is "stormfront" | "snowfront" | "dustfront" | "marshveil" {
  return effect === "stormfront" || effect === "snowfront" || effect === "dustfront" || effect === "marshveil";
}

export interface LivingWorldRoute {
  id: string;
  start: { x: number; y: number };
  finish: { x: number; y: number };
  radiusMetres: number;
  /** Actual owned-actor read-back; Unix milliseconds, never a projected position. */
  position?: { x: number; y: number; observedAt: number };
}

export function observedWeatherFrontPosition(route: LivingWorldRoute, now: number): LivingWorldRoute["position"] {
  const position = route.position;
  if (!position || ![position.x, position.y, position.observedAt, now].every(Number.isFinite)
    || position.observedAt > now || now - position.observedAt > 30_000) return undefined;
  const dx = route.finish.x - route.start.x;
  const dy = route.finish.y - route.start.y;
  const distanceSquared = dx * dx + dy * dy;
  if (!Number.isFinite(distanceSquared) || distanceSquared <= 0) return undefined;
  const x = position.x - route.start.x;
  const y = position.y - route.start.y;
  const progress = (x * dx + y * dy) / distanceSquared;
  const crossTrackMetres = Math.abs(x * dy - y * dx) / Math.sqrt(distanceSquared) / 100;
  // Native actors can return to the origin during fade-out. Do not plot that as weather.
  if (progress < -.05 || progress > 1.05 || crossTrackMetres > 10) return undefined;
  return { ...position };
}

export interface LivingWorldState {
  fronts?: Array<Omit<LivingWorldState, "fronts">>;
  available: boolean;
  effect: LivingWorldEffect;
  phase: string;
  region: "none" | "whole_island" | "targeted_clients" | "the_pit" | "highlands" | "east_swamp" | "west_swamp" | "river_delta" | "east_lake" | "west_coast" | "south_lake";
      regional?: { preset?: string; alpha?: number; ownership: string; mode: "automatic" | "manual" | "disabled"; next?: { preset: string; region: string; at: number }; upcoming?: Array<{ preset: string; region: string; at: number }>; aurora?: { chancePercent: number; night: number; status: "selected" | "not_selected" | "attempted" | "unavailable" } };
  observedAt: Date;
  endsAt?: Date;
  route?: LivingWorldRoute;
}

export interface ServerWeatherState {
  serverId: string;
  condition: WeatherCondition;
  intensity: number;
  source: WeatherSource;
  temperatureC?: number;
  windKph?: number;
  cloudCoverage?: number;
  rainIntensity?: number;
  fogIntensity?: number;
  windIntensity?: number;
  lightningIntensity?: number;
  timeOfDay?: number;
  rawWeatherName?: string;
  livingWorld?: LivingWorldState;
  forecastNote?: string;
  updatedByUserId?: UserId;
  observedAt: Date;
  updatedAt: Date;
}

export interface UpdateServerWeatherInput {
  serverId: string;
  condition: WeatherCondition;
  intensity?: number;
  source?: WeatherSource;
  temperatureC?: number;
  windKph?: number;
  cloudCoverage?: number;
  rainIntensity?: number;
  fogIntensity?: number;
  windIntensity?: number;
  lightningIntensity?: number;
  timeOfDay?: number;
  rawWeatherName?: string;
  livingWorld?: LivingWorldState;
  forecastNote?: string;
  updatedByUserId?: UserId;
  now: Date;
}

export function createDefaultWeatherState(serverId: string, now: Date): ServerWeatherState {
  return {
    serverId,
    condition: "unknown",
    intensity: 0,
    source: "unknown",
    observedAt: now,
    updatedAt: now
  };
}

export function updateServerWeatherState(input: UpdateServerWeatherInput): ServerWeatherState {
  const note = input.forecastNote?.trim();

  if (!input.serverId.trim()) {
    throw new Error("Weather state requires a server ID.");
  }

  return {
    serverId: input.serverId,
    condition: input.condition,
    intensity: clampPercent(input.intensity ?? 0),
    source: input.source ?? "manual",
    ...(typeof input.temperatureC === "number" ? { temperatureC: input.temperatureC } : {}),
    ...(typeof input.windKph === "number" ? { windKph: Math.max(0, Math.round(input.windKph)) } : {}),
    ...(typeof input.cloudCoverage === "number" ? { cloudCoverage: clampPercent(input.cloudCoverage) } : {}),
    ...(typeof input.rainIntensity === "number" ? { rainIntensity: clampPercent(input.rainIntensity) } : {}),
    ...(typeof input.fogIntensity === "number" ? { fogIntensity: clampPercent(input.fogIntensity) } : {}),
    ...(typeof input.windIntensity === "number" ? { windIntensity: clampPercent(input.windIntensity) } : {}),
    ...(typeof input.lightningIntensity === "number" ? { lightningIntensity: clampPercent(input.lightningIntensity) } : {}),
    ...(typeof input.timeOfDay === "number" ? { timeOfDay: input.timeOfDay } : {}),
    ...(input.rawWeatherName?.trim() ? { rawWeatherName: input.rawWeatherName.trim() } : {}),
    ...(input.livingWorld ? { livingWorld: validateLivingWorldState(input.livingWorld) } : {}),
    ...(note ? { forecastNote: note } : {}),
    ...(input.updatedByUserId ? { updatedByUserId: input.updatedByUserId } : {}),
    observedAt: input.now,
    updatedAt: input.now
  };
}

function validateLivingWorldState(input: LivingWorldState): LivingWorldState {
  const effects: LivingWorldEffect[] = ["none", "snow_frost_preview", "aurora_2d", "stormfront", "snowfront", "dustfront", "marshveil"];
  const regions: LivingWorldState["region"][] = ["none", "whole_island", "targeted_clients", "the_pit", "highlands", "east_swamp", "west_swamp", "river_delta", "east_lake", "west_coast", "south_lake"];
  if (!effects.includes(input.effect) || !regions.includes(input.region)) throw new Error("Living World state is invalid.");
  const phase = input.phase.trim();
  if (!phase || phase.length > 80 || !Number.isFinite(input.observedAt.getTime())) throw new Error("Living World evidence is invalid.");
  if (input.endsAt && !Number.isFinite(input.endsAt.getTime())) throw new Error("Living World deadline is invalid.");
  if (!input.available && (input.effect !== "none" || input.region !== "none")) throw new Error("Unavailable Living World state cannot claim an effect.");
  if (input.effect === "none" && input.region !== "none") throw new Error("Dormant Living World state cannot claim a region.");
  if (input.effect === "snow_frost_preview" && input.region !== "whole_island") throw new Error("Snow must use its reviewed whole-island region.");
  if (input.effect === "aurora_2d" && input.region !== "targeted_clients") throw new Error("Aurora must remain targeted.");
  if (isRegionalWeatherFront(input.effect) && ["none", "whole_island", "targeted_clients"].includes(input.region)) throw new Error("Regional weather requires a bounded region.");
  if (input.endsAt && input.endsAt.getTime() < input.observedAt.getTime()) throw new Error("Living World deadline predates its evidence.");
  let route: LivingWorldRoute | undefined;
  if (input.route) {
    const coordinates = [input.route.start.x, input.route.start.y, input.route.finish.x, input.route.finish.y, input.route.radiusMetres];
    if (!input.route.id.trim() || input.route.id.length > 80 || coordinates.some((value) => !Number.isFinite(value))
      || input.route.radiusMetres <= 0 || input.route.radiusMetres > 2_400) throw new Error("Living World route is invalid.");
    if (!isRegionalWeatherFront(input.effect)) throw new Error("Only the reviewed stormfront may publish a route.");
    const position = observedWeatherFrontPosition(input.route, input.observedAt.getTime());
    route = {
      id: input.route.id.trim(), start: { ...input.route.start }, finish: { ...input.route.finish },
      radiusMetres: input.route.radiusMetres, ...(position ? { position } : {})
    };
  }
  return {
    available: input.available,
    effect: input.effect,
    phase,
    region: input.region,
    observedAt: new Date(input.observedAt),
    ...(input.available && input.fronts ? { fronts: input.fronts.slice(0, 3).filter(front => front.available && isRegionalWeatherFront(front.effect)
      && front.observedAt.getTime() <= input.observedAt.getTime() && input.observedAt.getTime() - front.observedAt.getTime() <= 30_000)
      .map(front => validateLivingWorldState({ ...front, fronts: undefined })) } : {}),
    ...(input.endsAt ? { endsAt: new Date(input.endsAt) } : {}),
    ...(route ? { route } : {}),
    ...(input.regional ? { regional: validateRegionalWeather(input.regional, input.observedAt.getTime()) } : {})
  };
}

function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

export function validateRegionalWeather(input: NonNullable<LivingWorldState["regional"]>, observedAt: number): NonNullable<LivingWorldState["regional"]> {
  const presets = ["clear", "partly_cloudy", "cloudy", "overcast", "light_rain", "rain", "heavy_rain", "thunderstorm", "snow", "light_snow", "snowfall", "blizzard", "fog", "dust"];
  const regions = ["none", "highlands", "east_swamp", "west_swamp", "river_delta", "east_lake", "west_coast", "south_lake"];
  if (!["automatic", "manual", "disabled"].includes(input.mode) || !/^[a-z_]{1,60}$/.test(input.ownership)) throw new Error("Regional weather ownership is invalid.");
  if (input.preset !== undefined && !presets.includes(input.preset)) throw new Error("Regional weather preset is invalid.");
  if (input.alpha !== undefined && (!Number.isFinite(input.alpha) || input.alpha < 0 || input.alpha > 1)) throw new Error("Regional weather alpha is invalid.");
  const next = input.next;
  const validNext = next && presets.includes(next.preset) && regions.includes(next.region)
    && Number.isFinite(next.at) && next.at >= observedAt && next.at <= observedAt + 1_800_000;
  const upcoming = input.upcoming?.slice(0, 4).filter((entry, i, all) => presets.includes(entry.preset) && regions.includes(entry.region)
    && Number.isFinite(entry.at) && entry.at >= observedAt && entry.at <= observedAt + 7_200_000 && (i === 0 || entry.at > all[i - 1]!.at));
  const aurora = input.aurora;
  const validAurora = aurora && aurora.chancePercent === 25 && Number.isSafeInteger(aurora.night) && aurora.night >= 0
    && ["selected", "not_selected", "attempted", "unavailable"].includes(aurora.status);
  return { ownership: input.ownership, mode: input.mode,
    ...(upcoming?.length ? { upcoming } : {}), ...(validAurora ? { aurora: { ...aurora } } : {}),
    ...(input.preset !== undefined ? { preset: input.preset } : {}), ...(input.alpha !== undefined ? { alpha: input.alpha } : {}),
    ...(validNext ? { next: { ...next } } : {}) };
}
